import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  LayoutDashboard,
  Users,
  ArrowDownCircle,
  ArrowUpCircle,
  AppWindow,
  PlusCircle,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Bell,
  Wallet,
} from 'lucide-react';

const NAV_MAIN = [
  { to: '/admin',                   icon: LayoutDashboard,  label: 'Dashboard' },
  { to: '/admin/users',             icon: Users,            label: 'Users' },
  { to: '/admin/deposit-requests',  icon: ArrowDownCircle,  label: 'Deposit Requests' },
  { to: '/admin/withdraw-requests', icon: ArrowUpCircle,    label: 'Withdraw Requests' },
  { to: '/admin/deposit-addresses', icon: Wallet,           label: 'Deposit Addresses' },
];

const NAV_APPS = [
  { to: '/admin/apps',     icon: AppWindow,  label: 'Apps' },
  { to: '/admin/add-app',  icon: PlusCircle, label: 'Add New App' },
];

const NavItem = ({ item, isActive, onClick, badge }) => {
  const Icon = item.icon;
  const active = isActive(item.to);
  return (
    <li>
      <Link
        to={item.to}
        onClick={onClick}
        className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 relative
          ${active
            ? 'bg-cyan-500/10 text-cyan-400 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.15)]'
            : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
      >
        {active && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-cyan-400 rounded-r-full" />
        )}
        <Icon className={`shrink-0 ${active ? 'text-cyan-400' : 'text-gray-500 group-hover:text-gray-300'}`} size={18} />
        <span className="flex-1 truncate">{item.label}</span>
        {badge > 0 && (
          <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        {active && !badge && <ChevronRight size={14} className="text-cyan-400/50" />}
      </Link>
    </li>
  );
};

const SidebarInner = ({ isActive, onLinkClick, onLogout, user, pendingDeposits, pendingWithdrawals }) => (
  <div className="flex flex-col h-full">
    {/* Logo */}
    <div className="flex items-center gap-3 px-2 py-5 border-b border-white/5 mb-4">
      <div className="relative shrink-0">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/25">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l2.39 7.26H22l-6.19 4.5 2.36 7.24L12 17.27l-6.18 4.73 2.37-7.24L2 9.26h7.61z"/>
          </svg>
        </div>
      </div>
      <div>
        <span className="text-white font-bold text-base tracking-tight">RatingApp</span>
        <div className="text-[10px] text-gray-500 font-medium tracking-wider uppercase">Admin Panel</div>
      </div>
    </div>

    {/* Nav */}
    <nav className="flex-1 overflow-y-auto px-2 space-y-6">
      <div>
        <p className="px-3 mb-2 text-[10px] font-bold text-gray-600 tracking-widest uppercase">Main</p>
        <ul className="space-y-0.5">
          {NAV_MAIN.map(item => (
            <NavItem
              key={item.to}
              item={item}
              isActive={isActive}
              onClick={onLinkClick}
              badge={
                item.to === '/admin/deposit-requests'  ? pendingDeposits :
                item.to === '/admin/withdraw-requests' ? pendingWithdrawals : 0
              }
            />
          ))}
        </ul>
      </div>
      <div>
        <p className="px-3 mb-2 text-[10px] font-bold text-gray-600 tracking-widest uppercase">App Listing</p>
        <ul className="space-y-0.5">
          {NAV_APPS.map(item => (
            <NavItem key={item.to} item={item} isActive={isActive} onClick={onLinkClick} badge={0} />
          ))}
        </ul>
      </div>
    </nav>

    {/* User + Logout */}
    <div className="mt-4 pt-4 border-t border-white/5 px-2 space-y-1">
      {user && (
        <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/3 mb-1">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-cyan-400 flex items-center justify-center text-white text-xs font-bold shrink-0">
            {(user.username || user.name || 'A').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-medium truncate">{user.username || user.name}</div>
            <div className="text-[10px] text-gray-500 truncate capitalize">{user.role}</div>
          </div>
        </div>
      )}
      <button
        onClick={onLogout}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-red-500/8 transition-all group"
      >
        <LogOut size={16} className="shrink-0 group-hover:text-red-400" />
        Sign Out
      </button>
    </div>
  </div>
);

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingDeposits, setPendingDeposits] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const fetchPending = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      try {
        const [dep, wd] = await Promise.all([
          axios.get('/api/deposits/count/to-be-reviewed', cfg),
          axios.get('/api/withdrawals/count/to-be-reviewed', cfg),
        ]);
        setPendingDeposits(dep.data.count || 0);
        setPendingWithdrawals(wd.data.count || 0);
      } catch {}
    };
    fetchPending();
  }, [location.pathname]);

  const totalPending = pendingDeposits + pendingWithdrawals;

  const pageTitles = {
    '/admin': 'Dashboard',
    '/admin/users': 'Users',
    '/admin/deposit-requests': 'Deposit Requests',
    '/admin/withdraw-requests': 'Withdraw Requests',
    '/admin/deposit-addresses': 'Deposit Addresses',
    '/admin/apps': 'Apps',
    '/admin/add-app': 'Add New App',
  };
  const pageTitle = pageTitles[location.pathname] || 'Admin';

  return (
    <div className="min-h-screen bg-[#080e1a] text-white flex">

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-[#0d1526] border-r border-white/5 flex flex-col transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-white p-1.5 rounded-lg hover:bg-white/5 transition-all"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar"
        >
          <X size={18} />
        </button>
        <div className="flex-1 p-4 overflow-y-auto">
          <SidebarInner
            isActive={isActive}
            onLinkClick={() => setSidebarOpen(false)}
            onLogout={handleLogout}
            user={user}
            pendingDeposits={pendingDeposits}
            pendingWithdrawals={pendingWithdrawals}
          />
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:w-64 bg-[#0d1526] border-r border-white/5 flex-col">
        <div className="flex-1 p-4 overflow-y-auto">
          <SidebarInner
            isActive={isActive}
            onLinkClick={() => {}}
            onLogout={handleLogout}
            user={user}
            pendingDeposits={pendingDeposits}
            pendingWithdrawals={pendingWithdrawals}
          />
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64 flex flex-col flex-1 min-h-screen w-full">
        {/* Top bar */}
        <header className="sticky top-0 z-40 flex h-14 items-center gap-4 bg-[#080e1a]/90 backdrop-blur-md border-b border-white/5 px-4 sm:px-6">
          <button
            className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
          >
            <Menu size={20} />
          </button>

          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-600 text-sm hidden sm:inline">Admin</span>
            <ChevronRight size={14} className="text-gray-700 hidden sm:inline" />
            <span className="text-white text-sm font-semibold truncate">{pageTitle}</span>
          </div>

          <div className="ml-auto flex items-center gap-3">
            {totalPending > 0 ? (
              <Link
                to="/admin/deposit-requests"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400 hover:bg-red-500/15 transition-all"
              >
                <Bell size={13} className="animate-bounce" />
                {totalPending} pending review
              </Link>
            ) : (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/4 border border-white/8 text-xs text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                All clear
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
